// LabeledField — shared native form field for the NewTrip Stops card.
//
// Phase 12 UAT (2026-06-16): telegram-ui <Input> renders datetime-local blank
// (it forces appearance:none on the inner element) and its `header` label does
// not show outside a <Section>, so address / cargo / datetime looked
// inconsistent. This gives every stop field ONE native look: a visible label +
// a themed native <input> (var(--tgui--*) so it follows light/dark), keeping
// datetime-local fully functional.

import type React from "react";

import { TOUCH_TARGET_MIN } from "../lib/tokens";

/** Shared input styling — themed, fits its container, touch-target height. */
export const fieldInputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  minHeight: TOUCH_TARGET_MIN,
  padding: "10px 12px",
  fontSize: 16,
  color: "var(--tgui--text_color, #000)",
  background: "var(--tgui--secondary_bg_color, #efeff4)",
  border: "1px solid var(--tgui--outline, rgba(0,0,0,0.12))",
  borderRadius: 10,
  outline: "none",
};

/** datetime-local needs extra resets: iOS WebKit otherwise ignores width:100%
 *  and sizes the field to its content (date+time+stepper), overflowing the
 *  card (Phase 12 UAT 2026-06-16). WebkitAppearance:none makes iOS respect the
 *  box model. We KEEP an opaque bg + explicit colour + normal block flow, so —
 *  unlike telegram-ui's appearance:none + transparent + flex combo — the value
 *  stays visible and the native picker still opens on tap. */
export const datetimeInputStyle: React.CSSProperties = {
  ...fieldInputStyle,
  WebkitAppearance: "none",
  appearance: "none",
  minWidth: 0,
  maxWidth: "100%",
};

export function LabeledField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <label style={{ display: "block", marginTop: 8 }}>
      <span
        style={{
          display: "block",
          margin: "0 0 4px 4px",
          fontSize: 13,
          color: "var(--tgui--section_header_text_color, #707579)",
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}
